import { useCallback, useEffect, useState } from "react";
import { Check, MapPinned, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import CrmShell from "@/components/crm/CrmShell";
import LeadDrawer from "@/components/crm/LeadDrawer";
import { Panel, when } from "@/components/crm/ui";

export default function Schedule() {
  const [followups, setFollowups] = useState([]);
  const [visits, setVisits] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, v] = await Promise.all([
        api.get("/followups", { params: { window: "all" } }),
        api.get("/visits"),
      ]);
      setFollowups(f.data);
      setVisits(v.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const completeFollowUp = async (id) => {
    try {
      await api.patch(`/followups/${id}`);
      toast.success("Follow-up completed");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const completeVisit = async (id) => {
    try {
      await api.patch(`/visits/${id}?status=completed`);
      toast.success("Site visit marked complete");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <CrmShell title="Schedule" subtitle="Pending follow-ups and site visits">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel data-testid="followups-panel">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-brand" />
            <span className="text-xs font-bold uppercase tracking-wide text-muted2">
              Follow-ups ({followups.length})
            </span>
          </div>
          <div className="mt-4 space-y-2">
            {followups.length === 0 && <p className="text-xs text-muted2">Nothing pending.</p>}
            {followups.map((f) => (
              <div key={f.id} className="flex items-center justify-between rounded-xl border border-line bg-cloud p-3">
                <button onClick={() => setOpenId(f.lead_id)} data-testid={`schedule-followup-${f.id}`} className="text-left">
                  <div className="text-sm font-medium text-ink">{f.lead_name}</div>
                  <div className="mt-0.5 text-[11px] text-body">
                    {when(f.due_at)} · {f.channel} · {f.user_name}
                  </div>
                </button>
                <button
                  onClick={() => completeFollowUp(f.id)}
                  data-testid={`complete-followup-${f.id}`}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-brand/40 text-brand"
                  aria-label="Complete follow-up"
                >
                  <Check className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </Panel>

        <Panel data-testid="visits-panel">
          <div className="flex items-center gap-2">
            <MapPinned className="h-4 w-4 text-brand" />
            <span className="text-xs font-bold uppercase tracking-wide text-muted2">
              Site visits ({visits.length})
            </span>
          </div>
          <div className="mt-4 space-y-2">
            {visits.length === 0 && <p className="text-xs text-muted2">No visits booked.</p>}
            {visits.map((v) => (
              <div key={v.id} className="flex items-center justify-between rounded-xl border border-line bg-cloud p-3">
                <button onClick={() => setOpenId(v.lead_id)} data-testid={`schedule-visit-${v.id}`} className="text-left">
                  <div className="text-sm font-medium text-ink">{v.lead_name}</div>
                  <div className="mt-0.5 text-[11px] text-body">
                    {when(v.scheduled_at)} · {v.project}
                  </div>
                  {v.pickup && <div className="text-[11px] text-muted2">Pickup: {v.pickup}</div>}
                </button>
                {v.status === "scheduled" ? (
                  <button
                    onClick={() => completeVisit(v.id)}
                    data-testid={`complete-visit-${v.id}`}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-brand/40 text-brand"
                    aria-label="Mark visit complete"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider text-emerald-600">done</span>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <LeadDrawer leadId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} />
    </CrmShell>
  );
}
